/**
 * Patient Details Timer Hook
 * Persistent timer that tracks time spent on patients
 */
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/services/core/apiClient';

// Global state for patient timer
interface PatientTimerState {
  seconds: number;
  isRunning: boolean;
  isPaused: boolean;
  currentPatientId: number | null;
  timelogId: number | null;
  lastApiUpdate: number;
  isNavigating: boolean;
  autoStart: boolean;
}

let patientState: PatientTimerState = {
  seconds: 0,
  isRunning: false,
  isPaused: false,
  currentPatientId: null,
  timelogId: null,
  lastApiUpdate: 0,
  isNavigating: false,
  autoStart: true,
};

let patientTimerInterval: NodeJS.Timeout | null = null;
let currentPatientId: number | null = null;
const patientListeners: Set<() => void> = new Set();
const API_UPDATE_INTERVAL = 60;

const notifyPatientListeners = () => {
  patientListeners.forEach(listener => listener());
};

const PATIENT_TIMER_KEY = 'patient_timer_state';
const PATIENT_ID_KEY = 'patient_timer_id';

const savePatientState = () => {
  try {
    sessionStorage.setItem(PATIENT_TIMER_KEY, JSON.stringify({
      seconds: patientState.seconds,
      isRunning: patientState.isRunning,
      isPaused: patientState.isPaused,
      timelogId: patientState.timelogId,
      lastApiUpdate: patientState.lastApiUpdate,
    }));
    if (patientState.currentPatientId) {
      sessionStorage.setItem(PATIENT_ID_KEY, String(patientState.currentPatientId));
    }
  } catch (error) {
    console.error('Failed to save patient timer state:', error);
  }
};

const clearPatientState = () => {
  try {
    sessionStorage.removeItem(PATIENT_TIMER_KEY);
    sessionStorage.removeItem(PATIENT_ID_KEY);
  } catch (error) {
    console.error('Failed to clear patient timer state:', error);
  }
};

const globalPatientTimerCleanup = () => {
  if (patientTimerInterval) {
    clearInterval(patientTimerInterval);
    patientTimerInterval = null;
  }
  
  patientState = {
    seconds: 0,
    isRunning: false,
    isPaused: false,
    currentPatientId: null,
    timelogId: null,
    lastApiUpdate: 0,
    isNavigating: false,
    autoStart: true,
  };
  
  currentPatientId = null;
  clearPatientState();
  notifyPatientListeners();
};

const startPatientTimelog = async (userId: number, token: string): Promise<number | null> => {
  try {
    const response = await apiClient.post(`/api/pep/${userId}/timelog`, {
      duration: 0,
      status: "inprogress"
    }, token);
    
    if (response.data?.timeLogId) {
      return response.data.timeLogId;
    }
    return null;
  } catch (error) {
    console.error('Failed to start patient timelog:', error);
    return null;
  }
};

const updatePatientTimelog = async (userId: number, timelogId: number, duration: number, token: string): Promise<void> => {
  try {
    await apiClient.post(`/api/pep/${userId}/timelog/${timelogId}`, {
      duration,
      status: "inprogress"
    }, token);
  } catch (error) {
    console.error('Failed to update patient timelog:', error);
  }
};

const completePatientTimelog = async (userId: number, timelogId: number, duration: number, token: string): Promise<void> => {
  try {
    await apiClient.post(`/api/pep/${userId}/timelog/${timelogId}`, {
      duration,
      status: "completed"
    }, token);
  } catch (error) {
    console.error('Failed to complete patient timelog:', error);
  }
};

const startPatientTimer = (userId: number, token: string) => {
  if (patientTimerInterval) {
    clearInterval(patientTimerInterval);
  }

  patientTimerInterval = setInterval(async () => {
    if (patientState.isPaused || !patientState.isRunning) return;

    patientState.seconds++;
    
    if (patientState.seconds - patientState.lastApiUpdate >= API_UPDATE_INTERVAL) {
      patientState.lastApiUpdate = patientState.seconds;
      
      if (patientState.timelogId) {
        try {
          await updatePatientTimelog(userId, patientState.timelogId, patientState.seconds, token);
        } catch (error) {
          console.error('Failed to update patient timelog:', error);
        }
      }
    }
    
    savePatientState();
    notifyPatientListeners();
  }, 1000);
};

const stopPatientTimer = async (userId: number, token: string) => {
  if (patientTimerInterval) {
    clearInterval(patientTimerInterval);
    patientTimerInterval = null;
  }

  if (patientState.timelogId && patientState.seconds > 0) {
    try {
      await completePatientTimelog(userId, patientState.timelogId, patientState.seconds, token);
    } catch (error) {
      console.error('Failed to complete timelog on stop:', error);
    }
  }

  globalPatientTimerCleanup();
};

const togglePatientPause = () => {
  patientState.isPaused = !patientState.isPaused;
  savePatientState();
  notifyPatientListeners();
  
  // Emit sync event to global timer
  window.dispatchEvent(new CustomEvent('timerSync', { 
    detail: { source: 'patient', isPaused: patientState.isPaused } 
  }));
};

// Set pause state directly (for sync from global timer)
const setPatientPauseState = (isPaused: boolean) => {
  if (!patientState.isRunning) return;
  
  // Only change if state is different
  if (patientState.isPaused !== isPaused) {
    patientState.isPaused = isPaused;
    savePatientState();
    notifyPatientListeners();
  }
};

const formatPatientTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export const setPatientTimerNavigating = (isNavigating: boolean) => {
  patientState.isNavigating = isNavigating;
};

export const setPatientTimerAutoStart = (autoStart: boolean) => {
  patientState.autoStart = autoStart;
};

export const startPatientTimerForNewPatient = async (patientId: number, token: string, userId: number) => {
  if (patientTimerInterval) {
    clearInterval(patientTimerInterval);
    patientTimerInterval = null;
  }

  patientState = {
    seconds: 0,
    isRunning: false,
    isPaused: false,
    currentPatientId: patientId,
    timelogId: null,
    lastApiUpdate: 0,
    isNavigating: false,
    autoStart: patientState.autoStart,
  };

  currentPatientId = patientId;

  if (patientState.autoStart) {
    const timelogId = await startPatientTimelog(userId, token);
    
    if (timelogId) {
      patientState.timelogId = timelogId;
      patientState.isRunning = true;
      patientState.isPaused = false;
      
      startPatientTimer(userId, token);
    }
  }

  savePatientState();
  notifyPatientListeners();
};

export const resumeTimersForNewPatient = (globalWasPaused: boolean) => {
  if (globalWasPaused && patientState.isPaused) {
    patientState.isPaused = false;
    savePatientState();
    notifyPatientListeners();
  }
};

export const silentPatientTimerCleanup = () => {
  if (patientTimerInterval) {
    clearInterval(patientTimerInterval);
    patientTimerInterval = null;
  }
  
  patientState = {
    seconds: 0,
    isRunning: false,
    isPaused: false,
    currentPatientId: null,
    timelogId: null,
    lastApiUpdate: 0,
    isNavigating: false,
    autoStart: true,
  };
  
  currentPatientId = null;
  clearPatientState();
  notifyPatientListeners();
};

export const cleanupPatientTimer = silentPatientTimerCleanup;

export const usePatientDetailsTimer = (patientId: number | null) => {
  const { token, user } = useAuth();
  const [, forceUpdate] = useState({});

  useEffect(() => {
    const listener = () => forceUpdate({});
    patientListeners.add(listener);
    return () => {
      patientListeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (!patientId || !token || !user?.id) return;

    if (currentPatientId !== patientId) {
      startPatientTimerForNewPatient(patientId, token, Number(user.id));
    }
  }, [patientId, token, user?.id]);

  useEffect(() => {
    const handleLogout = () => {
      if (token && user?.id && patientState.isRunning) {
        stopPatientTimer(Number(user.id), token);
      }
    };

    window.addEventListener('beforeLogout', handleLogout);
    return () => {
      window.removeEventListener('beforeLogout', handleLogout);
    };
  }, [token, user?.id]);

  // Listen for timer sync events from global timer
  useEffect(() => {
    const handleTimerSync = (event: CustomEvent<{ source: string; isPaused: boolean }>) => {
      // Only sync if event came from global timer
      if (event.detail.source === 'global') {
        setPatientPauseState(event.detail.isPaused);
      }
    };
    
    window.addEventListener('timerSync', handleTimerSync as EventListener);
    return () => {
      window.removeEventListener('timerSync', handleTimerSync as EventListener);
    };
  }, []);

  const toggleTimer = useCallback(() => {
    togglePatientPause();
  }, []);

  const completeTimer = useCallback(async () => {
    if (token && user?.id && patientState.timelogId) {
      try {
        await completePatientTimelog(Number(user.id), patientState.timelogId, patientState.seconds, token);
        globalPatientTimerCleanup();
      } catch (error) {
        console.error('Failed to complete timer:', error);
      }
    }
  }, [token, user?.id]);

  return {
    seconds: patientState.seconds,
    isRunning: patientState.isRunning,
    isPaused: patientState.isPaused,
    formattedTime: formatPatientTime(patientState.seconds),
    toggleTimer,
    completeTimer,
    isVisible: patientState.isRunning && patientId === currentPatientId,
  };
};
